import React, { useEffect, useState } from 'react';
import { Modal } from 'semantic-ui-react';
import { SignOutAnswers, SignOutQuestionItem } from '../../types/SignOutQuestions';
import { areAllQuestionsAnswered } from '../../utils/signOutQuestions';
import './style.less';

interface SignOutQuestionsModalProps {
    open: boolean;
    questions: SignOutQuestionItem[];
    // True while the status change and the answers are being saved: the modal can't be dismissed then.
    submitting: boolean;
    // True once the answers are saved: they can't change and the modal can't be dismissed, only retried.
    locked: boolean;
    onCancel: () => void;
    onSubmit: (answers: SignOutAnswers) => void;
}

// Asked when a resident taps "I'm Out", the same questions the room device asks on sign-out. Free-text
// questions get an input and Yes/No ones a pair of buttons. Every question has to be answered.
const SignOutQuestionsModal: React.FC<SignOutQuestionsModalProps> = ({ open, questions, submitting, locked, onCancel, onSubmit }) => {
    const [answers, setAnswers] = useState<SignOutAnswers>({});

    // Each open is a fresh sign-out: don't carry over answers from a cancelled one.
    useEffect(() => {
        if (open) setAnswers({});
    }, [open]);

    const frozen = submitting || locked;
    const canSubmit = !submitting && areAllQuestionsAnswered(questions, answers);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (canSubmit) onSubmit(answers);
    };

    return (
        <Modal
            open={open}
            onClose={onCancel}
            className="sign-out-questions-modal"
            closeOnDimmerClick={!frozen}
            closeOnEscape={!frozen}
        >
            <form onSubmit={handleSubmit} noValidate>
                <Modal.Content className="sign-out-questions-body">
                    <div className="sign-out-questions-icon" aria-hidden="true">
                        <i className="fa-light fa-right-from-bracket" />
                    </div>
                    <div className="sign-out-questions-title">Before you head out</div>
                    <div className="sign-out-questions-sub">Please answer the questions below to mark yourself as out.</div>

                    <div className="mt-nbp-24 flex flex-col gap-nbp-16">
                        {questions.map(({ key, text, type }, index) => {
                            const labelId = `sign-out-question-${key}`;
                            const answer = answers[key] ?? '';
                            return (
                                <div className="flex flex-col gap-nbp-8" key={key}>
                                    <label className="sign-out-question-text" id={labelId} htmlFor={type === 'text' ? `${labelId}-input` : undefined}>
                                        {text}
                                    </label>
                                    {type === 'text' ? (
                                        <input
                                            id={`${labelId}-input`}
                                            type="text"
                                            className="sign-out-question-input"
                                            value={answer}
                                            placeholder="Type your answer"
                                            disabled={frozen}
                                            autoFocus={index === 0}
                                            autoComplete="off"
                                            onChange={(event) => {
                                                // Read it now: React 16 resets the event before the updater below runs.
                                                const { value } = event.target;
                                                setAnswers((prev) => ({ ...prev, [key]: value }));
                                            }}
                                        />
                                    ) : (
                                        <div className="flex gap-nbp-10" role="radiogroup" aria-labelledby={labelId}>
                                            {['Yes', 'No'].map((option) => (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={answer === option}
                                                    className={`sign-out-question-choice${answer === option ? ' selected' : ''}`}
                                                    disabled={frozen}
                                                    onClick={() => setAnswers((prev) => ({ ...prev, [key]: option }))}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Modal.Content>
                <Modal.Actions className="sign-out-questions-actions">
                    <button type="button" className="sign-out-questions-btn sign-out-questions-btn-cancel" disabled={frozen} onClick={onCancel}>
                        Cancel
                    </button>
                    <button type="submit" className="sign-out-questions-btn sign-out-questions-btn-submit" disabled={!canSubmit}>
                        {submitting && <span className="sign-out-questions-spinner" />}
                        I'm Out
                    </button>
                </Modal.Actions>
            </form>
        </Modal>
    );
};

export default SignOutQuestionsModal;
